import { AmountDisplay } from "@/app/shared/ui/amount-display";
import { PaymentHistory } from "./components/PaymentHistory";
import { Page } from "@/app/shared/components/page";
import { PageHeader } from "@groot/shell/components/layout";
import { RecordPaymentDialog } from "@/components/RecordPaymentDialog";
import { Badge } from "@groot/ui/badge";
import { Button } from "@groot/ui/button";
import { LoadingSpinner } from "@groot/ui/loading-spinner";
import { useBill } from "@/hooks/api/useBills";
import type { Bill } from "@/types";
import { formatBillingCycle } from "./formatBillingCycle";
import dayjs from "dayjs";
import {
  ArrowLeft,
  Bell,
  Calendar,
  CheckCircle2,
  CreditCard,
  Pencil,
  Receipt,
  RefreshCw,
} from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Helper to get status badge
function getStatusBadge(bill: Bill) {
  const nextDate = dayjs(bill.nextBillingDate);
  const today = dayjs();
  const daysUntil = nextDate.diff(today, "day");

  if (daysUntil < 0) {
    return (
      <Badge variant="destructive" className="font-normal">
        Overdue ({Math.abs(daysUntil)}d)
      </Badge>
    );
  }
  if (daysUntil === 0) {
    return (
      <Badge className="bg-warning/20 text-warning hover:bg-warning/20 font-normal">
        Due today
      </Badge>
    );
  }
  if (daysUntil <= 7) {
    return (
      <Badge className="bg-warning/20 text-warning hover:bg-warning/20 font-normal">
        Due in {daysUntil}d
      </Badge>
    );
  }

  return null;
}

export function BillDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);

  const { data: bill, isLoading } = useBill(Number(id));

  if (isLoading) {
    return (
      <Page maxWidth="5xl">
        <LoadingSpinner size="lg" className="py-20" />
      </Page>
    );
  }

  if (!bill) {
    return (
      <Page maxWidth="5xl">
        <p className="text-muted-foreground text-center py-20">Bill not found</p>
      </Page>
    );
  }

  const statusBadge = getStatusBadge(bill);
  const nextDueDate = dayjs(bill.nextBillingDate).format("MMMM D, YYYY");

  return (
    <Page maxWidth="5xl">
      {/* Header */}
      <div className="mb-8">
        <Button variant="ghost" onClick={() => navigate("/bills")} className="mb-4 -ml-2">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Bills
        </Button>
        <div className="flex items-center gap-4">
          <div className="size-12 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
            <Receipt className="size-6 text-muted-foreground" />
          </div>
          <PageHeader
            title={bill.name}
            description={
              <>
                <AmountDisplay amount={bill.amount} /> •{" "}
                {formatBillingCycle(bill.billingCycleType, bill.billingCycleValue)}
              </>
            }
            actions={
              <>
                {!bill.isActive && (
                  <Badge variant="secondary" className="font-normal">
                    Inactive
                  </Badge>
                )}
                {bill.trackingMode === "manual" && (
                  <Button
                    variant="outline"
                    onClick={() => setShowPaymentDialog(true)}
                    className="gap-2"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Mark as Paid
                  </Button>
                )}
                <Button onClick={() => navigate(`/bills/${bill.id}/edit`)} className="gap-2">
                  <Pencil className="w-4 h-4" />
                  Edit
                </Button>
              </>
            }
            className="flex-1"
          />
        </div>
      </div>

      {/* Next Due Card */}
      <div className="border border-border rounded-lg p-4 mb-6 bg-muted/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="size-5 text-muted-foreground/70" />
            <div>
              <p className="text-sm text-muted-foreground">Next due</p>
              <p className="text-base font-medium text-foreground">{nextDueDate}</p>
            </div>
          </div>
          {statusBadge}
        </div>
      </div>

      {/* Details Section */}
      <div className="border border-border rounded-lg p-6 mb-6">
        <h2 className="text-sm font-medium text-foreground mb-4">Details</h2>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <div>
            <dt className="text-sm text-muted-foreground flex items-center gap-2">
              <RefreshCw className="size-4" />
              Frequency
            </dt>
            <dd className="text-sm text-foreground mt-1">
              {formatBillingCycle(bill.billingCycleType, bill.billingCycleValue)}
            </dd>
          </div>

          <div>
            <dt className="text-sm text-muted-foreground flex items-center gap-2">
              <CreditCard className="size-4" />
              Payment Account
            </dt>
            <dd className="text-sm text-foreground mt-1">
              {bill.account?.accountName || "Not set"}
            </dd>
          </div>

          <div>
            <dt className="text-sm text-muted-foreground">Tracking Mode</dt>
            <dd className="text-sm text-foreground mt-1 capitalize">
              {bill.trackingMode === "manual" ? "Manual (Mark as paid)" : "Auto (On billing date)"}
            </dd>
          </div>

          <div>
            <dt className="text-sm text-muted-foreground flex items-center gap-2">
              <Bell className="size-4" />
              Reminders
            </dt>
            <dd className="text-sm text-foreground mt-1">
              {bill.receiveReminders
                ? bill.reminderType === "same_day"
                  ? "On billing date"
                  : `${bill.reminderDays} days before`
                : "Disabled"}
            </dd>
          </div>

          {bill.endDate && (
            <div>
              <dt className="text-sm text-muted-foreground">End Date</dt>
              <dd className="text-sm text-foreground mt-1">
                {dayjs(bill.endDate).format("MMMM D, YYYY")}
              </dd>
            </div>
          )}

          {bill.notes && (
            <div className="md:col-span-2">
              <dt className="text-sm text-muted-foreground">Notes</dt>
              <dd className="text-sm text-foreground mt-1">{bill.notes}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Payment History */}
      <PaymentHistory billId={bill.id} />

      {/* Record Payment Dialog */}
      <RecordPaymentDialog
        bill={showPaymentDialog ? bill : null}
        open={showPaymentDialog}
        onOpenChange={setShowPaymentDialog}
      />
    </Page>
  );
}
